import { X } from 'lucide-react';
import { useMemo } from 'react';
import {
  type Antibiotic,
  type CultureResult,
  ENUM_LABELS,
  type Organism,
  SUSCEPTIBILITY_CODES,
  type Susceptibility,
  toOptions,
} from '#shared/types';
import { Button } from '#client/components/ui/Button';
import { Card } from '#client/components/ui/Card';
import { Segmented } from '#client/components/ui/Segmented';
import { Select } from '#client/components/ui/Select';
import { TextField } from '#client/components/ui/TextField';
import { RESOURCE } from '#client/lib/routes';
import {
  type CultureDraft,
  withAntibiotic,
  withSensitivity,
  withoutAntibiotic,
} from './culture-entry.logic';
import { useAllRecords } from '#client/hooks/useAllRecords';

interface CultureCardsProps {
  stored: CultureResult;
  draft: CultureDraft;
  onChange: (update: (draft: CultureDraft) => CultureDraft) => void;
  readOnly: boolean;
}

/** What the bench usually writes; free text, so anything else can be typed. */
const SPECIMENS = ['Urine', 'Blood', 'Pus', 'Sputum', 'Stool', 'Wound swab', 'High vaginal swab'];
const COLONY_COUNTS = ['> 10^5 CFU/mL', '10^4 - 10^5 CFU/mL', '< 10^4 CFU/mL'];

/**
 * A culture report's entry: what was cultured and what grew, then the
 * antibiotic panel. The panel opens with the test's default antibiotics and
 * can be added to or trimmed per report.
 */
export function CultureResultsCards({ stored, draft, onChange, readOnly }: CultureCardsProps) {
  const organisms = useAllRecords<Organism>({ path: RESOURCE.organisms });
  const grew = draft.outcome === 'growth';

  return (
    <>
      <Card title="Culture" subtitle="What was cultured and what grew">
        <div className="c-form-grid">
          <TextField
            label="Specimen"
            value={draft.specimen}
            list="culture-specimens"
            disabled={readOnly}
            onChange={(event) => onChange((draft) => ({ ...draft, specimen: event.target.value }))}
          />
          <Select
            label="Outcome"
            placeholder="Not recorded yet"
            options={toOptions(ENUM_LABELS.cultureOutcome)}
            value={draft.outcome ?? ''}
            disabled={readOnly}
            onChange={(outcome) => onChange((draft) => ({ ...draft, outcome: outcome || null }))}
          />
          {grew && (
            <GrowthFields
              draft={draft}
              onChange={onChange}
              organisms={organisms.items}
              readOnly={readOnly}
            />
          )}
        </div>
        <datalist id="culture-specimens">
          {SPECIMENS.map((specimen) => (
            <option key={specimen} value={specimen} />
          ))}
        </datalist>
      </Card>

      {grew && (
        <SensitivityCard stored={stored} draft={draft} onChange={onChange} readOnly={readOnly} />
      )}
    </>
  );
}

function GrowthFields({
  draft,
  onChange,
  organisms,
  readOnly,
}: Omit<CultureCardsProps, 'stored'> & { organisms: Organism[] }) {
  return (
    <>
      <Select
        label="Organism isolated"
        placeholder="Select organism"
        options={organisms.map((organism) => ({
          value: String(organism.id),
          label: organism.name,
        }))}
        value={draft.organism_id === null ? '' : String(draft.organism_id)}
        disabled={readOnly}
        onChange={(choice) =>
          onChange((draft) => ({ ...draft, organism_id: choice === '' ? null : Number(choice) }))
        }
      />
      <Select
        label="Growth"
        placeholder="Not recorded"
        options={toOptions(ENUM_LABELS.growthGrade)}
        value={draft.growth ?? ''}
        disabled={readOnly}
        onChange={(growth) => onChange((draft) => ({ ...draft, growth: growth || null }))}
      />
      <TextField
        label="Colony count"
        value={draft.colony_count}
        list="culture-colony-counts"
        disabled={readOnly}
        onChange={(event) => onChange((draft) => ({ ...draft, colony_count: event.target.value }))}
      />
      <datalist id="culture-colony-counts">
        {COLONY_COUNTS.map((count) => (
          <option key={count} value={count} />
        ))}
      </datalist>
    </>
  );
}

const UNREAD = 'unread';
type Reading = Susceptibility | typeof UNREAD;

const READINGS: { value: Reading; label: string }[] = [
  { value: UNREAD, label: '—' },
  { value: 'sensitive', label: SUSCEPTIBILITY_CODES.sensitive },
  { value: 'intermediate', label: SUSCEPTIBILITY_CODES.intermediate },
  { value: 'resistant', label: SUSCEPTIBILITY_CODES.resistant },
];

/** The panel: one S / IS / R choice per antibiotic. Unread rows are not printed. */
function SensitivityCard({ stored, draft, onChange, readOnly }: CultureCardsProps) {
  const antibiotics = useAllRecords<Antibiotic>({ path: RESOURCE.antibiotics });

  // Names for the panel: the report's own antibiotics, then any added from the list.
  const nameById = useMemo(
    () =>
      new Map([
        ...antibiotics.items.map((antibiotic) => [antibiotic.id, antibiotic.name] as const),
        ...stored.sensitivities.map((row) => [row.antibiotic_id, row.antibiotic.name] as const),
      ]),
    [antibiotics.items, stored.sensitivities],
  );
  const panelAntibioticIds = new Set(draft.sensitivities.map((row) => row.antibiotic_id));
  const addable = antibiotics.items.filter((antibiotic) => !panelAntibioticIds.has(antibiotic.id));
  const read = draft.sensitivities.filter((row) => row.result !== null).length;

  return (
    <Card
      title="Sensitivity"
      subtitle={`${read} of ${draft.sensitivities.length} read — S sensitive, IS intermediate, R resistant`}
    >
      <ul className="c-culture__panel">
        {draft.sensitivities.map((row) => (
          <li key={row.antibiotic_id} className="c-culture__drug">
            <span>{nameById.get(row.antibiotic_id) ?? `Antibiotic ${row.antibiotic_id}`}</span>
            {readOnly ? (
              <strong>{row.result ? SUSCEPTIBILITY_CODES[row.result] : '—'}</strong>
            ) : (
              <span className="c-culture__controls">
                <Segmented<Reading>
                  label={`Result for ${nameById.get(row.antibiotic_id) ?? 'antibiotic'}`}
                  size="sm"
                  options={READINGS}
                  value={row.result ?? UNREAD}
                  onChange={(reading) =>
                    onChange((draft) =>
                      withSensitivity(
                        draft,
                        row.antibiotic_id,
                        reading === UNREAD ? null : reading,
                      ),
                    )
                  }
                />
                <Button
                  variant="ghost"
                  size="sm"
                  icon={<X />}
                  aria-label={`Remove ${nameById.get(row.antibiotic_id) ?? 'antibiotic'}`}
                  onClick={() => onChange((draft) => withoutAntibiotic(draft, row.antibiotic_id))}
                />
              </span>
            )}
          </li>
        ))}
      </ul>

      {!readOnly && addable.length > 0 && (
        <Select
          label="Add antibiotic"
          placeholder="Add an antibiotic to this report"
          options={addable.map((antibiotic) => ({
            value: String(antibiotic.id),
            label: antibiotic.name,
          }))}
          value=""
          onChange={(choice) => {
            if (choice !== '') onChange((draft) => withAntibiotic(draft, Number(choice)));
          }}
        />
      )}
    </Card>
  );
}
